/**
 * Sending a new account's touches, once (ATTR-1). SignupAttribution calls
 * recordSignup whenever a session appears for an account made in the last
 * 72 hours that this browser has not reported yet.
 *
 * gbp-agent-portal op signup_attribution writes the account's row once and
 * tells every configured ad platform in the background (CompleteRegistration).
 * Its answer carries the event id: the page's own tags (the Meta pixel, GTM)
 * fire the same conversion under it, so each platform counts one, and never on
 * a private page, which loads no tag.
 *
 * Never in the way: an 8 second budget on the call, failures logged and tried
 * again on a later page, three tries a tab, and every storage access wrapped.
 */
import { supabase } from '@/integrations/supabase/client';
import { isPrivateLocation } from '@/lib/privatePaths';
import { trackConversion } from '@/utils/tracking';
import { attributionPayload } from './keeper';

/** An account is a sign-up for this long after it was made (the server's ATTRIBUTION_WINDOW_HOURS). */
export const SIGNUP_FRESH_MS = 72 * 3_600_000;
/** The call's budget. */
export const RECORD_TIMEOUT_MS = 8000;
export const DONE_PREFIX = 'fly_attr_signup_done_v1:';
const TRIES_PREFIX = 'fly_attr_signup_tries_v1:';
const MAX_TRIES = 3;

/** Whether an account made at `createdAt` is still a new sign-up. */
export function isFreshAccount(createdAt: string | null | undefined, now = Date.now()): boolean {
  const made = Date.parse(String(createdAt ?? ''));
  return Number.isFinite(made) && now - made >= -5 * 60_000 && now - made < SIGNUP_FRESH_MS;
}

function readFlag(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeFlag(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* storage refused: at worst the server is asked again, and it answers already_recorded */
  }
}

/** True once this browser has had an answer for the account. */
export function signupReported(userId: string): boolean {
  return !!readFlag(localStorage, `${DONE_PREFIX}${userId}`);
}

/** Send this account's touches once; fire the page's own tags with the event id the server answers. */
export async function recordSignup(userId: string): Promise<void> {
  const tries = Number(readFlag(sessionStorage, `${TRIES_PREFIX}${userId}`) ?? 0);
  if (tries >= MAX_TRIES) return;
  writeFlag(sessionStorage, `${TRIES_PREFIX}${userId}`, String(tries + 1));
  try {
    const { data, error } = await supabase.functions.invoke('gbp-agent-portal', {
      body: { op: 'signup_attribution', ...attributionPayload() },
      timeout: RECORD_TIMEOUT_MS,
    });
    if (error || !data || (data as { ok?: boolean }).ok === false) {
      console.warn('[SignupAttribution] not recorded this time, asked again on a later page', error?.message ?? data);
      return;
    }
    writeFlag(localStorage, `${DONE_PREFIX}${userId}`, new Date().toISOString());
    const answer = data as { browser_event?: boolean; event_id?: string | null };
    if (answer.browser_event && typeof answer.event_id === 'string' && answer.event_id && !isPrivateLocation(window.location.pathname, window.location.search)) {
      trackConversion('CompleteRegistration', { eventId: answer.event_id });
    }
  } catch (e) {
    console.warn('[SignupAttribution] the call failed, asked again on a later page', e);
  }
}
